import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import type { ScheduleTask } from '../../services/schedulesService';
import { computeSummaryRollups } from './schedulingEngine';

/**
 * Prosty wykres Gantta w CSS (bez zewnętrznych libów), dopasowany kolorystycznie do
 * reszty aplikacji. Lewa kolumna — ID + opis, prawa strona — oś czasu z paskami.
 *
 * Oś czasu ma dwa poziomy nagłówków: miesiąc i dzień. Skala (szerokość jednego dnia)
 * jest konfigurowalna (zoom in/out).
 */

export interface ScheduleGanttProps {
  tasks: ScheduleTask[];
  stateDate?: string | null;
}

const ROW_HEIGHT = 32;
const HEADER_HEIGHT_TOP = 20;
const HEADER_HEIGHT_BOTTOM = 24;
const HEADER_TOTAL = HEADER_HEIGHT_TOP + HEADER_HEIGHT_BOTTOM;
const LEFT_COL_ID_WIDTH = 52;
const LEFT_COL_OPIS_WIDTH = 260;
const LEFT_TOTAL = LEFT_COL_ID_WIDTH + LEFT_COL_OPIS_WIDTH;

const MIN_DAY_PX = 6;
const MAX_DAY_PX = 60;
const DEFAULT_DAY_PX = 18;

function toDate(yyyyMmDd: string): Date {
  return new Date(`${yyyyMmDd}T12:00:00`);
}

function diffDays(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
}

function addDays(d: Date, n: number): Date {
  const copy = new Date(d.getTime());
  copy.setDate(copy.getDate() + n);
  return copy;
}

const MONTH_NAMES_PL = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień',
];

const ScheduleGantt: React.FC<ScheduleGanttProps> = ({ tasks, stateDate }) => {
  const [dayPx, setDayPx] = React.useState<number>(DEFAULT_DAY_PX);

  const rollups = React.useMemo(() => computeSummaryRollups(tasks), [tasks]);

  const { rangeStart, totalDays } = React.useMemo(() => {
    if (tasks.length === 0) {
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      const start = addDays(today, -7);
      const end = addDays(today, 21);
      return { rangeStart: start, totalDays: diffDays(end, start) + 1 };
    }
    let minIso = tasks[0].start_date;
    let maxIso = tasks[0].end_date;
    for (const t of tasks) {
      if (t.start_date < minIso) minIso = t.start_date;
      if (t.end_date > maxIso) maxIso = t.end_date;
    }
    const start = addDays(toDate(minIso), -3);
    const end = addDays(toDate(maxIso), 3);
    return { rangeStart: start, totalDays: diffDays(end, start) + 1 };
  }, [tasks]);

  const timelineWidth = totalDays * dayPx;

  /* Grupowanie osi po miesiącach na górny pasek. */
  const monthBands = React.useMemo(() => {
    const bands: { label: string; startDayIdx: number; spanDays: number }[] = [];
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(rangeStart, i);
      const month = d.getMonth();
      const year = d.getFullYear();
      const label = `${MONTH_NAMES_PL[month]} ${year}`;
      const last = bands[bands.length - 1];
      if (last && last.label === label) last.spanDays += 1;
      else bands.push({ label, startDayIdx: i, spanDays: 1 });
    }
    return bands;
  }, [rangeStart, totalDays]);

  const todayIdx = React.useMemo(() => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    return diffDays(today, rangeStart);
  }, [rangeStart]);

  const stateIdx = React.useMemo(() => {
    if (!stateDate) return null;
    return diffDays(toDate(stateDate), rangeStart);
  }, [stateDate, rangeStart]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        color: '#202124',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#FAFAFA',
          height: 34,
          flexShrink: 0,
        }}
      >
        <Tooltip title="Powiększ">
          <IconButton size="small" onClick={() => setDayPx((v) => Math.min(MAX_DAY_PX, Math.round(v * 1.25)))}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Pomniejsz">
          <IconButton size="small" onClick={() => setDayPx((v) => Math.max(MIN_DAY_PX, Math.round(v / 1.25)))}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ color: '#5F6368', fontSize: 12 }}>{dayPx}px / dzień · {totalDays} dni</Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', minWidth: LEFT_TOTAL + timelineWidth, position: 'relative' }}>
          {/* LEFT column (sticky) */}
          <Box
            sx={{
              width: LEFT_TOTAL,
              flexShrink: 0,
              position: 'sticky',
              left: 0,
              zIndex: 3,
              bgcolor: '#FFFFFF',
              borderRight: '2px solid #dadce0',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 4,
                height: HEADER_TOTAL,
                display: 'grid',
                gridTemplateColumns: `${LEFT_COL_ID_WIDTH}px ${LEFT_COL_OPIS_WIDTH}px`,
                bgcolor: '#F1F3F4',
                borderBottom: '1px solid #dadce0',
              }}
            >
              <Box sx={leftHeaderCell}>ID</Box>
              <Box sx={{ ...leftHeaderCell, justifyContent: 'flex-start' }}>Opis</Box>
            </Box>
            {tasks.map((t, idx) => {
              const r = rollups.get(t.id);
              const isSummary = !!r?.isSummary;
              const indent = (t.outline_level || 0) * 12;
              return (
                <Box
                  key={t.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `${LEFT_COL_ID_WIDTH}px ${LEFT_COL_OPIS_WIDTH}px`,
                    height: ROW_HEIGHT,
                    borderBottom: '1px solid #f1f3f4',
                    bgcolor: isSummary ? '#EEF0F8' : idx % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                  }}
                >
                  <Box sx={leftCellSx(true)}>{t.ordinal}</Box>
                  <Box
                    sx={{
                      ...leftCellSx(false),
                      paddingLeft: `${8 + indent}px`,
                      fontWeight: isSummary ? 600 : 400,
                      color: isSummary ? '#020242' : '#202124',
                    }}
                    title={t.opis}
                  >
                    {t.opis}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* TIMELINE */}
          <Box
            sx={{
              width: timelineWidth,
              flexShrink: 0,
              position: 'relative',
            }}
          >
            {/* Sticky header (months + days) */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: '#F1F3F4',
                borderBottom: '1px solid #dadce0',
                height: HEADER_TOTAL,
              }}
            >
              <Box sx={{ position: 'relative', height: HEADER_HEIGHT_TOP }}>
                {monthBands.map((b, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      left: b.startDayIdx * dayPx,
                      width: b.spanDays * dayPx,
                      height: HEADER_HEIGHT_TOP,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRight: '1px solid #dadce0',
                      fontWeight: 600,
                      color: '#3c4043',
                      fontSize: 11,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {b.label}
                  </Box>
                ))}
              </Box>
              <Box sx={{ position: 'relative', height: HEADER_HEIGHT_BOTTOM }}>
                {Array.from({ length: totalDays }).map((_, i) => {
                  const d = addDays(rangeStart, i);
                  const weekday = d.getDay();
                  const isWeekend = weekday === 0 || weekday === 6;
                  return (
                    <Box
                      key={i}
                      sx={{
                        position: 'absolute',
                        left: i * dayPx,
                        width: dayPx,
                        height: HEADER_HEIGHT_BOTTOM,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: '1px solid #e8eaed',
                        fontSize: 10,
                        color: isWeekend ? '#B00020' : '#5F6368',
                        bgcolor: isWeekend ? '#FFF3F3' : 'transparent',
                      }}
                    >
                      {d.getDate()}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Grid background (per-day columns + weekend highlight) */}
            <Box sx={{ position: 'absolute', top: HEADER_TOTAL, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: 'none' }}>
              {Array.from({ length: totalDays }).map((_, i) => {
                const d = addDays(rangeStart, i);
                const weekday = d.getDay();
                const isWeekend = weekday === 0 || weekday === 6;
                return (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: i * dayPx,
                      width: dayPx,
                      borderRight: '1px solid #f1f3f4',
                      bgcolor: isWeekend ? 'rgba(176,0,32,0.04)' : 'transparent',
                    }}
                  />
                );
              })}
            </Box>

            {/* Today vertical line — UWAGA: w sx `width: 1` oznacza 100%; trzeba użyć stringa '1px'. */}
            {todayIdx >= 0 && todayIdx <= totalDays && (
              <Box
                sx={{
                  position: 'absolute',
                  top: HEADER_TOTAL,
                  bottom: 0,
                  left: `${todayIdx * dayPx - 0.5}px`,
                  width: '1px',
                  bgcolor: '#1A73E8',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
                title={`Dziś: ${new Date().toISOString().slice(0, 10)}`}
              />
            )}

            {/* State date vertical line */}
            {stateIdx != null && stateIdx >= 0 && stateIdx <= totalDays && (
              <Box
                sx={{
                  position: 'absolute',
                  top: HEADER_TOTAL,
                  bottom: 0,
                  left: `${stateIdx * dayPx - 0.5}px`,
                  width: '1px',
                  borderLeft: '1px dashed #9C27B0',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
                title={`Data stanu: ${stateDate}`}
              />
            )}

            {/* Task rows */}
            <Box sx={{ position: 'relative' }}>
              {tasks.map((t, idx) => {
                const r = rollups.get(t.id);
                const isSummary = !!r?.isSummary;
                const startIso = isSummary && r ? r.start_date : t.start_date;
                const endIso = isSummary && r ? r.end_date : t.end_date;
                const progressPct = Math.max(
                  0,
                  Math.min(100, Number(isSummary && r ? r.progress_pct : t.progress_pct) || 0)
                );
                const startIdx = diffDays(toDate(startIso), rangeStart);
                const lengthDays = Math.max(1, diffDays(toDate(endIso), toDate(startIso)) + 1);
                return (
                  <Box
                    key={t.id}
                    sx={{
                      height: ROW_HEIGHT,
                      borderBottom: '1px solid #f1f3f4',
                      position: 'relative',
                      bgcolor: isSummary ? 'rgba(2,2,66,0.04)' : idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)',
                    }}
                  >
                    <Tooltip
                      title={`${t.ordinal} — ${t.opis} · ${startIso} → ${endIso} · ${progressPct}%`}
                      placement="top"
                      arrow
                    >
                      {isSummary ? (
                        /* Summary bar: wąski czarny pasek z „capami" po bokach (styl MS Project). */
                        <Box
                          sx={{
                            position: 'absolute',
                            top: ROW_HEIGHT / 2 - 5,
                            height: 10,
                            left: startIdx * dayPx,
                            width: lengthDays * dayPx,
                            pointerEvents: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 0,
                              right: 0,
                              height: 4,
                              bgcolor: '#020242',
                            }}
                          />
                          {/* Lewy „cap" */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: 0,
                              height: 0,
                              borderLeft: '5px solid #020242',
                              borderTop: '5px solid #020242',
                              borderRight: '0 solid transparent',
                              borderBottom: '5px solid transparent',
                            }}
                          />
                          {/* Prawy „cap" */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: 0,
                              height: 0,
                              borderRight: '5px solid #020242',
                              borderTop: '5px solid #020242',
                              borderLeft: '0 solid transparent',
                              borderBottom: '5px solid transparent',
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            height: ROW_HEIGHT - 8,
                            left: startIdx * dayPx,
                            width: lengthDays * dayPx,
                            bgcolor: '#E8EAF6',
                            border: '1px solid #3F51B5',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'default',
                          }}
                        >
                          <Box sx={{ height: '100%', width: `${progressPct}%`, bgcolor: '#3F51B5' }} />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 6,
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              color: progressPct > 50 ? '#fff' : '#1a237e',
                              fontSize: 11,
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              pointerEvents: 'none',
                            }}
                          >
                            {t.ordinal}. {t.opis}
                          </Box>
                        </Box>
                      )}
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const leftHeaderCell = {
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  color: '#3c4043',
  borderRight: '1px solid #e8eaed',
} as const;

const leftCellSx = (center: boolean) =>
  ({
    px: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: center ? 'center' : 'flex-start',
    borderRight: '1px solid #f1f3f4',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 12,
    color: '#202124',
  }) as const;

export default ScheduleGantt;
